import { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Stack, Title, Text, Button, ThemeIcon, Loader, PasswordInput, TextInput } from '@mantine/core';
import { IconCheck, IconX, IconLock, IconMail } from '@tabler/icons-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { applyActionCode, checkActionCode, confirmPasswordReset, signInWithEmailAndPassword, signInWithEmailLink } from 'firebase/auth';
import { auth } from '../firebase/config';
import { usePageTitle } from '../hooks/usePageTitle';
import { recallEmailForLink, forgetEmailForLink, resolveEmailForLink } from '../utils/emailLinkSignIn';

type Status = 'loading' | 'success' | 'error' | 'resetPassword' | 'recoverEmail' | 'confirmEmail';

export const AuthActionPage = () => {
  usePageTitle('Account | Meals You Love');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [successTitle, setSuccessTitle] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [successAction, setSuccessAction] = useState({ label: 'Continue', to: '/' });
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [linkEmail, setLinkEmail] = useState('');
  const [linkError, setLinkError] = useState('');
  const [signingIn, setSigningIn] = useState(false);

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  // The href as it arrived, kept because scrubAddressBar below strips the code
  // out of the live URL and signInWithEmailLink needs to see it.
  const originalHref = useRef(window.location.href);
  // Action codes are single-use. StrictMode double-invokes effects in dev, and
  // scrubbing the address bar changes searchParams, so guard on a ref rather
  // than letting either one burn the code twice.
  const hasHandled = useRef(false);

  /**
   * Redeem a sign-in link. This both signs the user in and marks their email
   * verified, so onboarding can resume immediately instead of bouncing them to
   * the landing page to authenticate again.
   */
  const redeemSignInLink = useCallback(async (email: string) => {
    await signInWithEmailLink(auth, email, originalHref.current);
    forgetEmailForLink();
    navigate('/', { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (hasHandled.current) return;
    hasHandled.current = true;

    if (!oobCode) {
      setStatus('error');
      setErrorMessage('Invalid or missing action link.');
      return;
    }

    // Drop the credential-bearing params from the visible URL before anything
    // records them. This page's URL otherwise reaches browser history, the
    // Referer header, PostHog autocapture, and session replay — and for a
    // sign-in link that URL is enough to take over the account.
    const scrubAddressBar = () => {
      const clean = new URL(window.location.href);
      ['oobCode', 'e', 'apiKey', 'continueUrl'].forEach((p) =>
        clean.searchParams.delete(p)
      );
      window.history.replaceState({}, '', clean.toString());
    };

    const handleAction = async () => {
      scrubAddressBar();
      try {
        switch (mode) {
          case 'signIn': {
            // Legacy: confirmation emails used to carry sign-in links, and some
            // are still sitting in inboxes. Validate the code up front (this
            // doesn't consume it) so a used or expired link goes straight to
            // the error state instead of asking for an address first and only
            // then reporting that the link is dead. Throws into the catch below.
            await checkActionCode(auth, oobCode);

            // The token in the link is the usual source: these are opened from
            // webmail in-app browsers far more often than from the browser that
            // requested them, and those have their own empty localStorage.
            // Read from the captured href, not searchParams: react-router
            // doesn't observe the raw replaceState above, so its copy is stale
            // in a way that's easy to trip over later.
            const linkToken = new URL(originalHref.current).searchParams.get('e');
            const address = linkToken
              ? await resolveEmailForLink(linkToken)
              : recallEmailForLink();

            if (!address) {
              setStatus('confirmEmail');
              return;
            }

            try {
              await redeemSignInLink(address);
            } catch (error: any) {
              if (error.code === 'auth/expired-action-code' || error.code === 'auth/invalid-action-code') {
                throw error;
              }
              setLinkEmail(address);
              setLinkError('We couldn\'t use that link automatically. Please confirm your email.');
              setStatus('confirmEmail');
            }
            return;
          }

          case 'verifyEmail': {
            // Wait for Firebase to restore any persisted session, otherwise
            // currentUser reads null on a fresh page load even when signed in.
            await auth.authStateReady();
            try {
              await applyActionCode(auth, oobCode);
            } catch (error: any) {
              // A reused link is harmless if the address is already verified;
              // treat it as success rather than an error.
              if (error.code !== 'auth/invalid-action-code' || !auth.currentUser) throw error;
              await auth.currentUser.reload();
              if (!auth.currentUser.emailVerified) throw error;
            }

            if (auth.currentUser) {
              await auth.currentUser.reload();
              // Refresh the ID token too so its email_verified claim matches.
              await auth.currentUser.getIdToken(true);
              // Same browser as the sign-up: pick onboarding back up.
              navigate('/', { replace: true });
              return;
            }

            // Opened somewhere with no session (phone, webmail in-app browser).
            // The password is intact, so signing in here works, but the tab
            // they signed up in will also continue once they click through.
            setSuccessTitle('Email verified!');
            setSuccessMessage('Head back to the tab where you signed up to continue or sign in here.');
            setSuccessAction({ label: 'Sign in', to: '/signin' });
            setStatus('success');
            break;
          }

          case 'resetPassword':
            // Verify the code is valid, then show the reset form
            const resetInfo = await checkActionCode(auth, oobCode);
            setResetEmail(resetInfo.data.email || '');
            setStatus('resetPassword');
            break;

          case 'recoverEmail':
            // Recover email shows info about the reverted email change
            const info = await checkActionCode(auth, oobCode);
            await applyActionCode(auth, oobCode);
            setSuccessTitle('Email restored');
            setSuccessMessage(
              info.data.email
                ? `Your email has been restored to ${info.data.email}. If you didn't change your email, consider resetting your password.`
                : 'Your email has been restored. If you didn\'t request this change, consider resetting your password.'
            );
            setStatus('success');
            break;

          default:
            setStatus('error');
            setErrorMessage('Unsupported action.');
        }
      } catch (error: any) {
        setStatus('error');
        if (error.code === 'auth/expired-action-code') {
          setErrorMessage('This link has expired. Please request a new one.');
        } else if (error.code === 'auth/invalid-action-code') {
          setErrorMessage(
            mode === 'verifyEmail' || mode === 'signIn'
              ? 'This link has already been used or is no longer valid. If you\'ve already confirmed your email, just sign in.'
              : 'This link is invalid or has already been used.'
          );
        } else {
          setErrorMessage('Something went wrong. Please try again.');
        }
      }
    };

    handleAction();
  }, [mode, oobCode, redeemSignInLink, navigate]);

  const handleConfirmEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setLinkError('');
    setSigningIn(true);
    try {
      await redeemSignInLink(linkEmail.trim());
    } catch (error: any) {
      if (error.code === 'auth/expired-action-code') {
        setLinkError('This link has expired. Please request a new one.');
      } else if (error.code === 'auth/invalid-action-code') {
        setLinkError('This link is invalid or has already been used.');
      } else {
        setLinkError('That email doesn\'t match this link. Please check and try again.');
      }
    } finally {
      setSigningIn(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    setResetting(true);
    try {
      await confirmPasswordReset(auth, oobCode!, newPassword);
      if (resetEmail) {
        await signInWithEmailAndPassword(auth, resetEmail, newPassword);
        navigate('/');
        return;
      }
      setSuccessTitle('Password reset!');
      setSuccessMessage('Your password has been updated. You can now sign in with your new password.');
      setStatus('success');
    } catch (error: any) {
      if (error.code === 'auth/expired-action-code') {
        setPasswordError('This link has expired. Please request a new password reset.');
      } else if (error.code === 'auth/weak-password') {
        setPasswordError('Password is too weak. Please choose a stronger password.');
      } else {
        setPasswordError('Something went wrong. Please try again.');
      }
    } finally {
      setResetting(false);
    }
  };

  return (
    <Container size="xs" py={80}>
      <Stack gap="xl" align="center" ta="center">
        {status === 'loading' && (
          <>
            <Loader color="orange" size="lg" />
            <Text size="lg" c="dimmed">Processing...</Text>
          </>
        )}

        {status === 'success' && (
          <>
            <ThemeIcon size={80} radius="xl" variant="gradient" gradient={{ from: 'green', to: 'teal' }}>
              <IconCheck size={40} />
            </ThemeIcon>
            <Stack gap="xs">
              <Title order={2} fw={700}>{successTitle}</Title>
              <Text size="lg" c="dimmed">{successMessage}</Text>
            </Stack>
            <Button
              size="md"
              color="orange"
              onClick={() => navigate(successAction.to)}
            >
              {successAction.label}
            </Button>
          </>
        )}

        {status === 'confirmEmail' && (
          <>
            <ThemeIcon size={80} radius="xl" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>
              <IconMail size={40} />
            </ThemeIcon>
            <Stack gap="xs">
              <Title order={2} fw={700}>Confirm your email</Title>
              <Text size="lg" c="dimmed">
                Enter the email address this link was sent to and we'll pick up where you left off.
              </Text>
            </Stack>
            <form onSubmit={handleConfirmEmail} style={{ width: '100%', maxWidth: 340 }}>
              <Stack gap="md" ta="left">
                <TextInput
                  label="Email"
                  type="email"
                  autoFocus
                  leftSection={<IconMail size={16} />}
                  value={linkEmail}
                  onChange={(e) => setLinkEmail(e.currentTarget.value)}
                  required
                />
                {linkError && <Text size="sm" c="red" ta="center">{linkError}</Text>}
                <Button
                  type="submit"
                  color="orange"
                  loading={signingIn}
                  disabled={!linkEmail.trim()}
                  fullWidth
                  mt="xs"
                >
                  Continue
                </Button>
              </Stack>
            </form>
          </>
        )}

        {status === 'resetPassword' && (
          <>
            <ThemeIcon size={80} radius="xl" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>
              <IconLock size={40} />
            </ThemeIcon>
            <Stack gap="xs">
              <Title order={2} fw={700}>Reset your password</Title>
              <Text size="lg" c="dimmed">Enter your new password below.</Text>
            </Stack>
            <form onSubmit={handlePasswordReset} style={{ width: '100%', maxWidth: 340 }}>
              <Stack gap="md" ta="left">
                <PasswordInput
                  label="New password"
                  leftSection={<IconLock size={16} color={newPassword ? (newPassword.length >= 6 ? 'var(--mantine-color-green-6)' : 'var(--mantine-color-red-6)') : undefined} />}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.currentTarget.value)}
                  required
                />
                <PasswordInput
                  label="Confirm password"
                  leftSection={<IconLock size={16} color={confirmPassword ? (newPassword === confirmPassword ? 'var(--mantine-color-green-6)' : 'var(--mantine-color-red-6)') : undefined} />}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.currentTarget.value)}
                  required
                />
                {passwordError && <Text size="sm" c="red" ta="center">{passwordError}</Text>}
                <Button
                  type="submit"
                  color="orange"
                  loading={resetting}
                  disabled={newPassword.length < 6 || newPassword !== confirmPassword}
                  fullWidth
                  mt="xs"
                >
                  Reset password
                </Button>
              </Stack>
            </form>
          </>
        )}

        {status === 'error' && (
          <>
            <ThemeIcon size={80} radius="xl" color="red" variant="light">
              <IconX size={40} />
            </ThemeIcon>
            <Stack gap="xs">
              <Title order={2} fw={700}>Something went wrong</Title>
              <Text size="lg" c="dimmed">{errorMessage}</Text>
            </Stack>
            <Button
              size="md"
              color="orange"
              onClick={() => navigate('/')}
            >
              Go home
            </Button>
          </>
        )}
      </Stack>
    </Container>
  );
};
